import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSurvey } from '../../../../contexts/SurveyContext';
import { LoadingSpinner } from '../../../../components/common/LoadingSpinner';
import { Toast } from '../../../../components/common/Toast';
import { TimelineHeader } from './timeline/TimelineHeader';
import { formatDistance } from '../../../../utils/routeHelpers';
import { TimelineMap } from './timeline/TimelineMap';
import { Timeline } from './timeline/Timeline';
import { loadRouteData } from '../../utils/routeDataLoader';
import { logError, logInfo } from '../../../../utils/logger';

export const RouteTimelinePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeSurvey } = useSurvey();

  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [pois, setPois] = useState([]);
  const [toast, setToast] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [error, setError] = useState(null);

  // Sort POIs by creation date
  const sortedPois = React.useMemo(() => {
    return [...pois].sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [pois]);

  useEffect(() => {
    if (!location.state?.surveyId && !activeSurvey) {
      logInfo('No survey selected, redirecting to roadscope');
      navigate('/roadscope');
      return;
    }
    loadData();
  }, [location.state, activeSurvey, navigate]);

  const loadData = async () => {
    try {
      const surveyId = location.state?.surveyId || activeSurvey?.id;
      if (!surveyId) return;

      logInfo(`Loading route data for survey: ${surveyId}`);
      const { routes: loadedRoutes, pois: loadedPois } = await loadRouteData(surveyId);
      
      setRoutes(loadedRoutes);
      setPois(loadedPois);
      
      if (loadedRoutes.length > 0) {
        logInfo(`Setting initial route: ${loadedRoutes[0].id}`);
        setSelectedRoute(loadedRoutes[0]);
      }
      logInfo(`Loaded ${loadedRoutes.length} routes and ${loadedPois.length} POIs`);
    } catch (error) {
      logError('Error loading route data:', error);
      setError(error.message || 'Failed to load route data');
      setToast({
        type: 'error',
        message: 'Failed to load route data'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRouteSelect = (route) => {
    if (mapInstance) {
      logInfo(`Selected route: ${route.id}`);
      mapInstance.setZoom(12);
      setSelectedRoute(route);

      // Center map on route immediately after setting the route
      if (route.trackPoints?.length) {
        const bounds = new window.google.maps.LatLngBounds();
        route.trackPoints.forEach(point => {
          bounds.extend({
            lat: Number(point.latitude),
            lng: Number(point.longitude)
          });
        });
        mapInstance.fitBounds(bounds, {
          padding: { top: 50, right: 50, bottom: 50, left: 50 }
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#3D3F42] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#3D3F42] flex items-center justify-center p-4">
        <div className="bg-[#2A2B2E] p-6 rounded-lg text-center max-w-md">
          <h2 className="text-xl font-semibold text-red-500 mb-2">Error Loading Route Data</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/roadscope')}
            className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
          >
            Return to RoadScope
          </button>
        </div>
      </div>
    );
  }

  if (routes.length === 0) {
    return (
      <div className="min-h-screen bg-[#3D3F42]">
        <TimelineHeader 
          onBack={() => navigate(-1)} 
          surveyName={activeSurvey?.name} 
        />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="bg-[#2A2B2E] p-6 rounded-lg text-center max-w-md">
            <h2 className="text-xl font-semibold text-white mb-2">No Routes Found</h2>
            <p className="text-gray-400 mb-4">This survey doesn't have any routes yet. Start recording a route to see it here.</p>
            <button
              onClick={() => navigate('/roadscope')}
              className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
            >
              Return to RoadScope
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#3D3F42]">
      <TimelineHeader 
        onBack={() => navigate(-1)} 
        surveyName={activeSurvey?.name} 
      />

      <div className="min-h-[calc(100vh-64px)]">
        <div className="bg-[#1a1a1a] p-4">
          <select 
            value={selectedRoute?.id || ''}
            onChange={(e) => {
              const route = routes.find(r => r.id === e.target.value);
              handleRouteSelect(route);
            }}
            className="w-full bg-[#2A2B2E] text-white border border-gray-700 rounded-lg p-3"
          >
            {routes.map(route => (
              <option key={route.id} value={route.id}>
                {`${route.name || `Imported Route ${route.id.slice(-8)}`} (${route.poiCount || 0} POIs • ${formatDistance(route.distance)})`}
              </option>
            ))}
          </select>
        </div>

        <TimelineMap
          route={selectedRoute || null}
          pois={selectedRoute ? pois : sortedPois}
          onLoad={setMapInstance}
        />
        <Timeline
          route={selectedRoute || null}
          pois={selectedRoute ? pois : sortedPois}
          showAllPois={!selectedRoute && sortedPois.length > 0}
        />
      </div>
    
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default RouteTimelinePage;